import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, X, Loader2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Kpi, Block, fdate, inputCls, btnGold, btnGhost } from './ui';
import { useConfirm } from '../ConfirmDialog';

/*
  Contratos de aportación a operaciones (04/11/2026). Solo superadmin.
  1. Eliges la operación y sueltas todos sus PDF de golpe.
  2. El sistema propone a qué socio va cada uno (email o DNI dentro del PDF, o su nombre en el archivo).
  3. Revisas, corriges lo que haga falta y guardas. Cada socio lo ve en «Mis documentos».
*/
const opt = 'bg-[#0d1829]';
const HINT = { good: 'text-emerald-300 border-emerald-400/20 bg-emerald-400/10', warn: 'text-amber-200 border-amber-400/20 bg-amber-400/10', info: 'text-sky-200 border-sky-400/20 bg-sky-400/10' };
const Hint = ({ tone, children }) => <span className={`inline-block max-w-[280px] rounded-lg border px-2 py-1 text-[11px] leading-snug ${HINT[tone]}`}>{children}</span>;

function MemberSelect({ value, onChange, candidates, opName, testid }) {
  const inOp = candidates.filter((c) => c.in_operation);
  const rest = candidates.filter((c) => !c.in_operation);
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} w-full min-w-[220px] py-1.5`} data-testid={testid}>
      <option value="" className={opt}>Elige el socio…</option>
      {inOp.length > 0 && (
        <optgroup label={`Aportantes de ${opName}`} className={opt}>
          {inOp.map((c) => <option key={c.id} value={c.id} className={opt}>{c.name} · {c.id}</option>)}
        </optgroup>
      )}
      <optgroup label="Otros socios del cuadro de mando" className={opt}>
        {rest.map((c) => <option key={c.id} value={c.id} className={opt}>{c.name} · {c.id}</option>)}
      </optgroup>
    </select>
  );
}

async function openPdf(getUrl) {
  const w = window.open('', '_blank');
  try {
    const url = await getUrl();
    if (w) w.location.href = url; else window.location.href = url;
  } catch (e) {
    if (w) w.close();
    toast.error(e.message);
  }
}

export default function CdmContratos() {
  const [opId, setOpId] = useState(null);
  const { data, error, reload } = useLoad(() => dashboardApi.contracts(opId), [opId]);
  const [review, setReview] = useState(null); // { files: [...] } tras analizar
  const [assign, setAssign] = useState({});
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const [dialog, ask] = useConfirm();

  const candidates = data?.candidates || [];
  const names = useMemo(() => Object.fromEntries(candidates.map((c) => [c.id, c.name])), [candidates]);
  const op = (data?.operations || []).find((o) => o.id === data?.operation_id);
  const opName = op?.name || data?.operation_id || '';

  if (!data) return <Loading error={error} />;
  if (!data.operations.length) return <p className="rounded-xl border border-dashed border-white/[0.08] p-6 text-sm text-gray-400">Aún no hay operaciones en el cuadro de mando.</p>;

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.size > 0);
    if (!files.length) return;
    setBusy(true);
    try {
      const r = await dashboardApi.contractsAnalyze(data.operation_id, files);
      const a = {};
      r.files.forEach((f) => { if (f.id) a[f.id] = f.proposal?.member_id || ''; });
      setAssign(a);
      setReview(r);
      const ok = r.files.filter((f) => f.id).length;
      toast.success(`${ok} PDF listos para revisar`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const pending = (review?.files || []).filter((f) => f.id);
  const rejected = (review?.files || []).filter((f) => !f.id);
  const unassigned = pending.filter((f) => !assign[f.id]).length;
  const assignedTwice = (() => {
    const seen = {}; const dup = new Set();
    pending.forEach((f) => { const m = assign[f.id]; if (m) { if (seen[m]) dup.add(m); seen[m] = true; } });
    return dup;
  })();

  const removeRow = (id) => {
    setReview((r) => ({ ...r, files: r.files.filter((f) => f.id !== id) }));
    setAssign((a) => { const n = { ...a }; delete n[id]; return n; });
    dashboardApi.contractsConfirm({ operation_id: data.operation_id, items: [], discard: [id] }).catch(() => {});
  };

  const save = async () => {
    setBusy(true);
    try {
      const items = pending.map((f) => ({ id: f.id, member_id: assign[f.id] }));
      const r = await dashboardApi.contractsConfirm({ operation_id: data.operation_id, items, discard: [] });
      toast.success(`${r.saved} contratos guardados. Cada socio ya los ve en «Mis documentos».`);
      setReview(null);
      setAssign({});
      reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const discardAll = async () => {
    const ids = pending.map((f) => f.id);
    setReview(null);
    setAssign({});
    if (ids.length) await dashboardApi.contractsConfirm({ operation_id: data.operation_id, items: [], discard: ids }).catch(() => {});
  };

  const reassign = async (c, memberId) => {
    if (!memberId || memberId === c.member_id) return;
    try {
      await dashboardApi.contractReassign(c.id, memberId);
      toast.success(`Contrato asignado a ${names[memberId] || memberId}`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (c) => {
    const ok = await ask({
      title: 'Eliminar este contrato',
      details: [{ label: 'Socio', value: `${c.member_name} · ${c.member_id}` }, { label: 'Archivo', value: c.filename },
        'El socio deja de verlo en «Mis documentos». El PDF se borra de la plataforma (guarda tu copia).'],
      confirmLabel: 'Eliminar contrato',
    });
    if (!ok) return;
    try { await dashboardApi.contractDelete(c.id); toast.success('Contrato eliminado'); reload(); } catch (e) { toast.error(e.message); }
  };

  const inOp = candidates.filter((c) => c.in_operation).length;
  return (
    <div className="space-y-6" data-testid="cdm-contratos">
      {dialog}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold">Contratos de aportación</h3>
          <p className="text-xs text-gray-400 mt-1 max-w-2xl">Sube los contratos firmados de una operación. Cada socio verá el suyo en «Mis documentos»; quien aún no tiene cuenta, en cuanto se registre con su email.</p>
        </div>
        <label className="text-xs text-gray-400 space-y-1">
          <span className="block">Operación</span>
          <select value={data.operation_id} onChange={(e) => { setReview(null); setOpId(e.target.value); }} className={`${inputCls} min-w-[240px]`} data-testid="contracts-op">
            {data.operations.map((o) => <option key={o.id} value={o.id} className={opt}>{o.id} · {o.name}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label="Aportantes" value={inOp} sub={`en ${opName}`} />
        <Kpi label="Con contrato" value={data.contracts.length} sub="PDF guardados" tone="good" />
        <Kpi label="Faltan" value={data.missing.length} sub="aportantes sin contrato" tone={data.missing.length ? 'bad' : 'good'} />
      </div>

      {!review && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? 'border-[#c9a84c] bg-[#c9a84c]/[0.06]' : 'border-white/15 hover:border-[#c9a84c]/50'}`}
          data-testid="contracts-drop">
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} disabled={busy} data-testid="contracts-input" />
          {busy ? <Loader2 className="animate-spin text-[#c9a84c]" size={28} /> : <Upload className="text-[#c9a84c]" size={28} />}
          <p className="text-sm font-semibold text-white">{busy ? 'Leyendo los PDF…' : 'Arrastra aquí todos los PDF o pulsa para elegirlos'}</p>
          <p className="text-xs text-gray-400">Contratos de {opName} · solo PDF, hasta 10 MB cada uno, máximo 60 por subida</p>
        </label>
      )}

      {review && (
        <Block title={`Revisa a quién va cada contrato (${pending.length})`}
          right={<button type="button" onClick={discardAll} className={btnGhost} disabled={busy}><X size={14} /> Cancelar subida</button>}>
          <p className="text-xs text-gray-400">Comprueba el socio de cada PDF (puedes abrirlo pulsando su nombre). Corrige los que no tengan socio o estén mal y pulsa Guardar.</p>
          <Table rowKey="id" rows={pending} cols={[
            { key: 'filename', label: 'Archivo', noSort: true, render: (f) => (
              <button type="button" onClick={() => openPdf(() => dashboardApi.contractFileUrl(f.id))} className="inline-flex items-center gap-1.5 text-left text-white hover:text-[#e3c87a]">
                <FileText size={14} className="shrink-0 text-[#c9a84c]" /> <span className="break-all">{f.filename}</span>
              </button>) },
            { key: 'member', label: 'Socio', noSort: true, render: (f) => (
              <MemberSelect value={assign[f.id]} onChange={(v) => setAssign((a) => ({ ...a, [f.id]: v }))} candidates={candidates} opName={opName} testid="contracts-assign" />) },
            { key: 'reason', label: 'Cómo lo ha encontrado', noSort: true, render: (f) => (
              <div className="space-y-1">
                {f.proposal?.member_id && assign[f.id] === f.proposal.member_id
                  ? <Hint tone="good">{f.proposal.reason}</Hint>
                  : assign[f.id] ? <Hint tone="info">Elegido a mano</Hint> : <Hint tone="warn">{f.proposal?.reason || 'Elige el socio'}</Hint>}
                {f.text_found === false && <p className="text-[11px] text-gray-400">PDF escaneado: sin texto que leer</p>}
                {f.warning && assign[f.id] === f.proposal?.member_id && <p className="text-[11px] text-amber-300">{f.warning}</p>}
                {assign[f.id] && assignedTwice.has(assign[f.id]) && <p className="text-[11px] text-amber-300">Este socio tiene varios PDF en esta subida</p>}
              </div>) },
            { key: 'x', label: '', noSort: true, render: (f) => (
              <button type="button" onClick={() => removeRow(f.id)} aria-label={`Quitar ${f.filename}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-white/[0.06] hover:text-white"><X size={15} /></button>) },
          ]} />
          {rejected.length > 0 && (
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-3 text-xs text-amber-200 space-y-1">
              <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={13} /> No se han subido:</p>
              {rejected.map((f) => <p key={f.filename}>{f.filename}: {f.error}</p>)}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {unassigned > 0 && <span className="text-xs text-amber-300">{unassigned} sin socio: elígelo o quítalo de la lista</span>}
            <button type="button" onClick={save} disabled={busy || !pending.length || unassigned > 0} className={btnGold} data-testid="contracts-save">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Guardar {pending.length} contratos
            </button>
          </div>
        </Block>
      )}

      {data.missing.length > 0 && (
        <Block title={`Aportantes de ${opName} sin contrato (${data.missing.length})`}>
          <div className="flex flex-wrap gap-2">
            {data.missing.map((m) => <span key={m.id} className="rounded-full border border-white/10 px-3 py-1 text-xs text-gray-300">{m.name} · {m.id}</span>)}
          </div>
        </Block>
      )}

      <Block title={`Contratos guardados (${data.contracts.length})`}>
        <Table rowKey="id" rows={data.contracts} empty="Todavía no hay contratos de esta operación." cols={[
          { key: 'member_name', label: 'Socio', render: (c) => (
            <MemberSelect value={c.member_id} onChange={(v) => reassign(c, v)} candidates={candidates} opName={opName} testid="contracts-member" />) },
          { key: 'filename', label: 'Archivo', render: (c) => <span className="break-all text-gray-300">{c.filename}</span> },
          { key: 'confirmed_at', label: 'Subido', render: (c) => fdate(c.confirmed_at || c.uploaded_at) },
          { key: 'acts', label: '', noSort: true, render: (c) => (
            <div className="flex items-center justify-end gap-1">
              <button type="button" onClick={() => openPdf(() => dashboardApi.contractFileUrl(c.id))} className={btnGhost}><ExternalLink size={14} /> Ver</button>
              <button type="button" onClick={() => remove(c)} aria-label={`Eliminar el contrato de ${c.member_name}`} className="rounded-lg p-2 text-gray-400 hover:bg-red-500/10 hover:text-red-300"><Trash2 size={15} /></button>
            </div>) },
        ]} />
      </Block>
    </div>
  );
}
